
import { Component, Prop } from 'vue-property-decorator';
import VWrapper from '@/components/shared/VWrapper.vue';
import GeneralForm from '@/components/promo/form/GeneralForm.vue';
import { mixins } from 'vue-class-component';
import StackedForm from '@/mixins/StackedForm';
import Editable from '@/mixins/Editable';
import Notification from '@/mixins/Notification';
import { namespace } from 'vuex-class';
import { PromoCode } from '@/interfaces/models/PromoCode';
import { Venue } from '@/interfaces/models/Venue';
import { Article as ArticleModel } from '@/interfaces/models/Article';
import EditPromoCodeLoader from '@/components/promo/EditPromoCodeLoader.vue';
import { AxiosResponse } from 'axios';
import FoodcardApiService from '@/api/http/FoodcardApiService';
import SimpleGeneralForm from '@/components/promo/form/SimpleGeneralForm.vue';
import { User } from '@/interfaces/models/User';
import { VenueLabel } from '@/interfaces/models/VenueLabel';
import { Permission } from '@/enums/Permission';
import { CustomerGroup } from '@/enums/CustomerGroup';
import AvailabilityForm from '@/components/promo/form/AvailabilityForm.vue';
import { PromoCodeTeedeli } from '@/interfaces/models/PromoCodeTeedeli';
import { PiggyReward } from '@/interfaces/models/PiggyReward';
import { Tag } from '@/interfaces/models/Tag';
import ImageForm from '@/components/promo/form/ImageForm.vue';
import AvailableHourForm from '@/components/promo/form/AvailableHourForm.vue';
import BannerForm from '@/components/promo/form/BannerForm.vue';
import { formDataAppendObject } from '@/util/helper';
import Filter from '@/interfaces/api/Filter';

const promo = namespace('promo');
const app = namespace('app');
const auth = namespace('auth');
const venue = namespace('venue');
const label = namespace('venueLabel');
const reward = namespace('reward');
const tag = namespace('tag');

@Component({
  components: {
    SimpleGeneralForm,
    EditPromoCodeLoader,
    GeneralForm,
    VWrapper,
    AvailabilityForm,
    ImageForm,
    AvailableHourForm,
    BannerForm,
  },
})
export default class EditPromoCode extends mixins(Editable, Notification, StackedForm) {
  @Prop({ type: Boolean, default: false }) public multiple!: boolean;
  @Prop({ type: Boolean, default: false }) public teedeli!: boolean;

  public $refs!: {
    general: InstanceType<typeof GeneralForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
    };
    image: InstanceType<typeof ImageForm> & { getData: () => any };
    availability: InstanceType<typeof AvailabilityForm> & { getData: () => any };
    availableHours: InstanceType<typeof AvailableHourForm> & { getData: () => any };
    banner: InstanceType<typeof BannerForm> & { getData: () => any };
  };

  @app.State('venues') public venues!: Venue[];
  @auth.State('user') public authUser!: User;
  @venue.State('active') public venue!: Venue;
  @promo.State('active') public active!: PromoCode;
  @label.State('items') public labels!: VenueLabel[];
  @label.Action('fetch') public getVenueLabels!: any;

  @tag.State('items') public tags!: Tag[];
  @tag.Action('setFilter') public setTagFilter!: (f: Filter) => void;
  @tag.Action('fetch') public getTags!: any;

  @promo.Action('uploadGiftCards') public uploadGiftCards!: any;
  @promo.Action('store') public store!: any;
  @promo.Action('storeTeedeliCode') public storeTeedeliCode!: any;
  @promo.Action('generate') public generateCodes!: any;
  @promo.Action('show') public show!: any;
  @promo.Action('update') public update!: any;
  @promo.Action('uploadImage') public uploadImage!: any;

  @reward.Action('getByCustomerGroup') public getRewards!: any;
  @reward.Action('resetRewards') public resetRewards!: any;
  @reward.Action('setFilter') public setRewardsFilter!: any;
  @reward.State('items') public rewards!: PiggyReward[];

  public venuesByGroupAndCountry: Venue[] = [];
  public articles: Array<Partial<ArticleModel>> = [];
  public tagsList: Tag[] = [];

  get customerGroup() {
    if (this.authUser.customerGroup && this.authUser.customerGroup !== CustomerGroup.Default) {
      return this.authUser.customerGroup;
    }
    if (this.authUser.linkedCustomerGroup && this.authUser.linkedCustomerGroup !== CustomerGroup.Default) {
      return this.authUser.linkedCustomerGroup;
    }

    return null;
  }

  get linkedCustomerGroup() {
    if (this.authUser.linkedCustomerGroup && this.authUser.linkedCustomerGroup !== CustomerGroup.Default) {
      return this.authUser.linkedCustomerGroup;
    }

    if (this.authUser.customerGroup && this.authUser.customerGroup !== CustomerGroup.Default) {
      return this.authUser.customerGroup;
    }

    return null;
  }

  get promoCode() {
    if (this.editing && this.active) {
      return {
        ...this.active,
        ...(this.active.from || this.active.to
          ? { range: { from: this.active.from || null, to: this.active.to || null } }
          : {}),
      };
    }

    return null;
  }

  get title() {
    return this.editing ? 'promo.edit' : 'promo.create';
  }

  get venuesSorted(): Venue[] {
    return this.venuesByGroupAndCountry;
  }

  get filteredRewards() {
    return this.rewards.map((reward: PiggyReward) => {
      reward.disable = false;
      return reward;
    });
  }

  public async mounted() {
    this.$startLoading('promo');
    await this.resetRewards();

    if (this.$can(Permission.LABEL_VIEW)) {
      await this.getVenueLabels();
    }

    if (this.editing) {
      await this.show({ id: this.id });

      if (this.promoCode) {
        await this.getRewardsData(this.promoCode.venues, this.promoCode.customerGroup as CustomerGroup);
      }
    }

    if (this.editing && !this.active) {
      this.$router.push({ name: 'promo.index', query: { ...this.$route.query } });
    }

    this.$stopLoading('promo');
    this.venuesByGroupAndCountry = this.venues;
  }

  public async onCountriesOrCustomerGroupChange(data: {
    countries: string[];
    customerGroup: CustomerGroup;
    linkedCustomerGroup: CustomerGroup;
  }) {
    const customerGroup: CustomerGroup = data.customerGroup
      ? data.customerGroup
      : (this.customerGroup as CustomerGroup);

    if (data.countries?.length) {
      this.venuesByGroupAndCountry = this.venues.filter((venue: Venue) => {
        if (data.countries.find((country: string) => venue.country === country)) {
          return venue;
        }
      });
    } else {
      this.venuesByGroupAndCountry = this.venues;
    }

    if (customerGroup) {
      this.venuesByGroupAndCountry = this.venuesByGroupAndCountry.filter(
        (venue: Venue) => venue.customerGroup === customerGroup,
      );
    }
  }

  public async onVenuesChange(data: { venues: string[]; countries: string[]; customerGroup: CustomerGroup }) {
    const foodcardApi: FoodcardApiService = new FoodcardApiService();
    let articles: Array<Partial<ArticleModel>> = [];

    await this.getRewardsData(data.venues, data.customerGroup);

    this.$startLoading('article');

    for (const id of data.venues) {
      const articleRes: AxiosResponse<Array<Partial<ArticleModel>>> = await foodcardApi.getArticleNames({
        venue: id,
      });
      articles = [...articles, ...articleRes.data];
    }

    this.articles = articles;

    await this.getTagsData(data.venues);
    this.$stopLoading('article');
  }

  public async getTagsData(venues: string[] = []) {
    const venueIds: string[] = venues || [];
    let tags: Tag[] = [];

    await Promise.all(
      venueIds.map(async (venueId) => {
        this.setTagFilter({ venue: venueId });
        await this.getTags();

        tags = [...tags, ...(this.tags || [])];
      }),
    );

    this.tagsList = tags;
  }

  public async getRewardsData(venues: string[], customerGroup: CustomerGroup) {
    const availableCustomerGroup: CustomerGroup = customerGroup || (this.customerGroup as CustomerGroup);

    if (!availableCustomerGroup) {
      return;
    }

    const venuesFiltered: Venue[] =
      venues?.length > 0 && this.venues?.length > 0
        ? this.venues?.filter((venue: Venue) => venues.includes(venue._id))
        : [];
    const isPiggyLoyaltyEnabled: boolean =
      venuesFiltered.length > 0 && venuesFiltered.every((venue: Venue) => venue.piggyLoyaltyEnabled === true);

    if (!isPiggyLoyaltyEnabled) {
      this.setRewardsFilter({});
      this.resetRewards();
    } else {
      this.setRewardsFilter({ customerGroup: availableCustomerGroup });
      setTimeout(async () => {
        await this.getRewards();
      }, 0);
    }
  }

  public save() {
    this.$refs.general.validate().then(async (res: boolean | boolean[]) => {
      if (!this.isValid(res)) {
        this.notifyError('notification.form');
        return;
      }

      try {
        this.$startLoading('promo.save');
        const data: Partial<PromoCode> = { ...this.getMergedData() };

        if (this.editing) {
          await this.update({ ...data, id: this.active!._id });
          await this.handleUploadCsv();
        } else {
          if (this.multiple) {
            const formData: FormData = new FormData();

            formData.append('file', data.file as Blob);
            delete data.file;

            formDataAppendObject(formData, data, 'code');
            formData.append('amount', `${data.generateAmount || 10}`);

            await this.generateCodes(formData);
          } else {
            if (this.teedeli) {
              await this.storeTeedeliCode({ ...this.getMergedTeedeliData() });
            } else {
              const formData: FormData = new FormData();

              formDataAppendObject(formData, data);

              await this.store(formData);
              await this.handleUploadCsv();
            }
          }
        }

        if (!this.multiple && data.image && data.image instanceof File) {
          data.id = this.active!._id;

          if (data?.id) {
            const formData: FormData = new FormData();
            formData.append('asset', data.image);

            await this.uploadImage({ ...data, image: formData });
          }
        }

        this.$router.push({ name: 'promo.index', query: { ...this.$route.query } });
      } finally {
        this.$stopLoading('promo.save');
      }
    });
  }

  protected getMergedData(): Partial<PromoCode> {
    return {
      ...this.$refs.general.getData(),
      ...this.$refs.image.getData(),
      ...this.$refs.availableHours.getData(),
      ...this.$refs.banner.getData(),
      availability: {
        ...this.$refs.availability.getData(),
      },
    };
  }

  protected getMergedTeedeliData(): Partial<PromoCodeTeedeli> {
    return {
      amount: this.$refs.general.getData().amountPromo,
      code: {
        ...this.$refs.general.getData(),
        availability: {
          ...this.$refs.availability.getData(),
        },
      },
    };
  }

  private async handleUploadCsv(): Promise<void> {
    const formData: FormData = new FormData();
    const uploadedCsv = this.$refs.general.getData().uploadedGiftCards;
    const needToUpload = uploadedCsv && uploadedCsv instanceof File;

    if (needToUpload && uploadedCsv) {
      formData.set('file', uploadedCsv);
      await this.uploadGiftCards({ id: this.active!._id, file: formData });
    }
  }
}
